import React, { ChangeEvent } from 'react';
import FlosField, { type FlosFieldProps } from '../../flos-field';
import type { FlosInputProps } from '../../input/flos-input';
import { SelectInput, type SelectChangeDetails, type SelectChangeHandler, type SelectInputProps } from './custom-select-input';

export type SelectFieldProps = Omit<SelectInputProps, 'onChange'> &
    Omit<FlosFieldProps, 'custom' | 'pattern' | 'maxLength' | 'minLength' | 'showLength' | 'isOpen' | 'editable'> & {
        /**
         * @deprecated use `onSelectionChange`
         */
        onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
    };

/**
 * `SelectField` is a composition of `FlosField` component and `SelectInput` components. If you need extra customization, you can compose them yourself.
 */
export const SelectField = React.forwardRef<HTMLSelectElement, SelectFieldProps>(
    ({ isValid, onValidityChange, errorText = 'skal udfyldes', wrapperClassName, helpText, onSelectionChange, onChange, ...selectProps }, ref) => {
        const { disabled, iconShape, id, label, required, 'data-testid': dataTestId, onBlur, onFocus, onKeyDown, onKeyUp, ...restSelectProps } = selectProps;

        const selectInputProps: SelectInputProps = {
            ...restSelectProps,
            disabled,
            id,
            label,
            required,
            'data-testid': dataTestId,
        };

        return (
            <FlosField
                ref={ref}
                isValid={isValid}
                onValidityChange={onValidityChange}
                label={label}
                errorText={errorText}
                required={required}
                id={id}
                hasDropdown={true}
                wrapperClassName={wrapperClassName}
                disabled={disabled}
                helpText={helpText}
                iconShape={iconShape}
                renderInput={({ getInputProps, markFieldDirty }) => {
                    const fieldInputProps = getInputProps({
                        onBlur,
                        onFocus,
                        onKeyDown,
                        onKeyUp,
                    } as FlosInputProps);

                    const {
                        ref: fieldRef,
                        id: fieldId,
                        onBlur: fieldOnBlur,
                        onFocus: fieldOnFocus,
                        onKeyDown: fieldOnKeyDown,
                        onKeyUp: fieldOnKeyUp,
                        'aria-describedby': ariaDescribedBy,
                    } = fieldInputProps as FlosInputProps;

                    const handleSelectionChange: SelectChangeHandler = (details: SelectChangeDetails) => {
                        markFieldDirty(details.value);
                        onSelectionChange?.(details);
                        if (onChange) {
                            // TODO: deprecate this synthetic event creation
                            // We need to create a synthetic event to match the signature of a normal select input change event
                            // This is not ideal, but it is the best we can do without breaking existing implementations
                            // We should consider removing this in future major releases
                            const name = details.name ?? selectInputProps.name;
                            const syntheticEvent = {
                                target: { value: details.value, name },
                                currentTarget: { value: details.value, name },
                                preventDefault: () => console.warn('This is a synthetic event. preventDefault has no effect, you should use `onSelectionChange` instead.'),
                                stopPropagation: () => console.warn('This is a synthetic event. stopPropagation has no effect, you should use `onSelectionChange` instead.'),
                            } as unknown as ChangeEvent<HTMLSelectElement>;

                            onChange(syntheticEvent);
                        }
                    };

                    return (
                        <SelectInput
                            {...selectInputProps}
                            id={fieldId}
                            ref={fieldRef as unknown as React.Ref<HTMLButtonElement>}
                            onBlur={fieldOnBlur as React.FocusEventHandler<HTMLButtonElement>}
                            onFocus={fieldOnFocus as React.FocusEventHandler<HTMLButtonElement>}
                            onKeyDown={fieldOnKeyDown as React.KeyboardEventHandler<HTMLButtonElement>}
                            onKeyUp={fieldOnKeyUp as React.KeyboardEventHandler<HTMLButtonElement>}
                            aria-describedby={ariaDescribedBy}
                            label={label}
                            onSelectionChange={handleSelectionChange}
                        />
                    );
                }}
                data-testid={dataTestId}
            />
        );
    }
);

SelectField.displayName = 'SelectField';
